import React from "react";
import { Box, Sheet } from "@mui/joy";
import DarkTypography from "../../../common/DarkTypography";
import type { MailPlaceholderDefinition, MailTemplate } from "api/mailTemplates/MailTemplate.typed";
import { mailEditorEnvelopeStripSheetSx } from "../../../../styles/globalStyles";
import {
  MAIL_ENVELOPE_CONTROL_HEIGHT_PX,
  mailPlaceholderPresentableLabel,
} from "../mailEditorShared";
import {
  parseHtmlToMailLineParts,
  parsePlainStringToMailLineParts,
  mailPhToken,
  serializeMailLinePartsToSubject,
} from "../mailLineParts";
import type { MailLinePart } from "../mailLineParts";
import type { MailPlaceholderInsertTarget } from "../mailPlaceholderContext";
import {
  insertMailPhChipAtCaretInContentEditable,
  insertPlainTextAtCaretInContentEditable,
} from "../mailContentEditable";
import CuratedEmojiPicker from "../../../common/CuratedEmojiPicker";
import MailRecipientEditor from "./MailRecipientEditor";
import MailRichLineEditor from "./MailRichLineEditor";

type MailEditorEnvelopeStripProps = {
  template: MailTemplate | undefined;
  onTemplateChange: (next: MailTemplate) => void;
  canEdit: boolean;
  placeholderCatalog: MailPlaceholderDefinition[];
  subjectPlaceholderChips: MailPlaceholderDefinition[];
  recipientPlaceholderChips: MailPlaceholderDefinition[];
  onPlaceholderTargetChange: (target: MailPlaceholderInsertTarget) => void;
  registerSubjectInsert: (fn: ((key: string) => void) | null) => void;
};

type EnvelopeRecipientFieldProps = {
  template: MailTemplate | undefined;
  onTemplateChange: (next: MailTemplate) => void;
  canEdit: boolean;
  field: "to" | "bcc";
  placeholderCatalog: MailPlaceholderDefinition[];
  recipientPlaceholderChips: MailPlaceholderDefinition[];
  onPlaceholderTargetChange: (target: MailPlaceholderInsertTarget) => void;
};

function EnvelopeRecipientField(props: EnvelopeRecipientFieldProps) {
  const {
    template,
    onTemplateChange,
    canEdit,
    field,
    placeholderCatalog,
    recipientPlaceholderChips,
    onPlaceholderTargetChange,
  } = props;
  const list = template?.[field] ?? [];

  const onAdd =
    canEdit && template
      ? (value: string) => onTemplateChange({ ...template, [field]: [...list, value] })
      : undefined;

  const onRemove =
    canEdit && template
      ? (index: number) =>
          onTemplateChange({
            ...template,
            [field]: list.filter((_: string, idx: number) => idx !== index),
          })
      : undefined;

  return (
    <MailRecipientEditor
      list={list}
      onAdd={onAdd}
      onRemove={onRemove}
      readOnly={!canEdit}
      placeholderDropCatalog={placeholderCatalog}
      placeholderDropAllowedDefinitions={recipientPlaceholderChips}
      valueLabelCatalog={placeholderCatalog}
      rowHeightPx={MAIL_ENVELOPE_CONTROL_HEIGHT_PX}
      onFieldFocus={() => onPlaceholderTargetChange(field)}
    />
  );
}

const envelopeStackSx = {
  display: "flex",
  flexDirection: "column",
  gap: { xs: 1, sm: 1.25 },
  width: "100%",
  minWidth: 0,
} as const;

const envelopeFieldLabelSx = {
  lineHeight: 1.2,
  mb: 0.35,
  minHeight: "1.1em",
} as const;

const subjectCellSx = {
  width: "100%",
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
} as const;

const recipientCellSx = {
  flex: "1 1 200px",
  minWidth: 0,
  maxWidth: "100%",
  display: "flex",
  flexDirection: "column",
} as const;

const recipientsRowSx = {
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  alignItems: "flex-start",
  gap: { xs: 1, sm: 1.25 },
  width: "100%",
  minWidth: 0,
} as const;

export default function MailEditorEnvelopeStrip(props: MailEditorEnvelopeStripProps) {
  const {
    template,
    onTemplateChange,
    canEdit,
    placeholderCatalog,
    subjectPlaceholderChips,
    recipientPlaceholderChips,
    onPlaceholderTargetChange,
    registerSubjectInsert,
  } = props;

  const subjectEditorElRef = React.useRef<HTMLDivElement | null>(null);
  const templateRef = React.useRef(template);
  const onTemplateChangeRef = React.useRef(onTemplateChange);
  const catalogRef = React.useRef(placeholderCatalog);

  templateRef.current = template;
  onTemplateChangeRef.current = onTemplateChange;
  catalogRef.current = placeholderCatalog;

  const subjectParts = React.useMemo(
    () => parsePlainStringToMailLineParts(template?.subject || ""),
    [template?.subject],
  );

  const presentablePhLabel = React.useCallback(
    (key: string) => mailPlaceholderPresentableLabel(key, catalogRef.current),
    [],
  );

  const commitSubjectParts = React.useCallback((parts: MailLinePart[]) => {
    const current = templateRef.current;

    if (!current) {
      return;
    }

    const next = serializeMailLinePartsToSubject(parts);

    if (next === current.subject) {
      return;
    }

    onTemplateChangeRef.current({ ...current, subject: next });
  }, []);

  const setSubjectFieldRef = React.useCallback((_key: string, el: HTMLElement | null) => {
    subjectEditorElRef.current = el instanceof HTMLDivElement ? el : null;
  }, []);

  React.useEffect(() => {
    registerSubjectInsert((key) => {
      const current = templateRef.current;

      if (!current) {
        return;
      }

      const el = subjectEditorElRef.current;
      const label = mailPlaceholderPresentableLabel(key, catalogRef.current);

      if (el?.isContentEditable) {
        insertMailPhChipAtCaretInContentEditable(el, key, "none", label);
        commitSubjectParts(parseHtmlToMailLineParts(el.innerHTML));

        return;
      }

      const token = mailPhToken(key);
      const next = `${current.subject || ""}${token}`;

      onTemplateChangeRef.current({ ...current, subject: next });
    });

    return () => registerSubjectInsert(null);
  }, [commitSubjectParts, registerSubjectInsert]);

  return (
    <Sheet variant="outlined" sx={mailEditorEnvelopeStripSheetSx}>
      <Box sx={envelopeStackSx}>
        <Box sx={subjectCellSx}>
          <DarkTypography level="body-xs" colorVariant="muted" sx={envelopeFieldLabelSx}>
            Sujet
          </DarkTypography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, width: "100%", minWidth: 0 }}>
            <Box sx={{ flex: "1 1 auto", minWidth: 0 }}>
              <MailRichLineEditor
                parts={subjectParts}
                onChange={commitSubjectParts}
                canEdit={canEdit && !!template}
                blockId="envelope-subject"
                ariaLabel="Sujet"
                setFieldRef={setSubjectFieldRef}
                trackFocus={() => onPlaceholderTargetChange("subject")}
                variant="subject"
                placeholder="Sujet"
                presentablePhLabel={presentablePhLabel}
                placeholderCatalog={
                  subjectPlaceholderChips.length > 0 ? subjectPlaceholderChips : placeholderCatalog
                }
                minRowsText={1}
              />
            </Box>
            {canEdit && template ? (
              <CuratedEmojiPicker
                triggerSizePx={32}
                menuZIndex={2500}
                onPick={(emoji) => {
                  const el = subjectEditorElRef.current;

                  if (el?.isContentEditable) {
                    insertPlainTextAtCaretInContentEditable(el, emoji);
                    commitSubjectParts(parseHtmlToMailLineParts(el.innerHTML));

                    return;
                  }

                  onTemplateChange({ ...template, subject: `${template.subject || ""}${emoji}` });
                }}
              />
            ) : null}
          </Box>
        </Box>
        <Box sx={recipientsRowSx}>
          <Box sx={recipientCellSx}>
            <DarkTypography level="body-xs" colorVariant="muted" sx={envelopeFieldLabelSx}>
              À
            </DarkTypography>
            <EnvelopeRecipientField
              template={template}
              onTemplateChange={onTemplateChange}
              canEdit={canEdit}
              field="to"
              placeholderCatalog={placeholderCatalog}
              recipientPlaceholderChips={recipientPlaceholderChips}
              onPlaceholderTargetChange={onPlaceholderTargetChange}
            />
          </Box>
          <Box sx={recipientCellSx}>
            <DarkTypography level="body-xs" colorVariant="muted" sx={envelopeFieldLabelSx}>
              Cci
            </DarkTypography>
            <EnvelopeRecipientField
              template={template}
              onTemplateChange={onTemplateChange}
              canEdit={canEdit}
              field="bcc"
              placeholderCatalog={placeholderCatalog}
              recipientPlaceholderChips={recipientPlaceholderChips}
              onPlaceholderTargetChange={onPlaceholderTargetChange}
            />
          </Box>
        </Box>
      </Box>
    </Sheet>
  );
}
